import { ReactElement, useRef } from 'react';

import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { Dayjs } from 'dayjs';

import { DateField } from '@mui/x-date-pickers/DateField';

import { MonthDay, SeasonMonthDays } from '../../types/Org';
import { dayjsToMonthDay, monthDayToDayjs } from '../../utils/utils';

/**
 * Calculates the default end month/day as one day before the start month/day.
 */
function getDefaultEndMonthDay(start: MonthDay): MonthDay {
  const startDate = monthDayToDayjs(start);
  const endDate = startDate.subtract(1, 'day');
  return { month: endDate.month() + 1, day: endDate.date() };
}

interface SeasonMonthDaysRowProps {
  seasonMonthDays: SeasonMonthDays;
  index: number;
  canDelete: boolean;
  onChange: (updated: SeasonMonthDays) => void;
  onDelete: () => void;
}

function SeasonMonthDaysRow({
  seasonMonthDays,
  index,
  canDelete,
  onChange,
  onDelete,
}: SeasonMonthDaysRowProps): ReactElement {
  // Track if user has manually edited the end date
  const endDateCustomized = useRef(false);

  const handleStartChange = (date: Dayjs | null) => {
    const start = dayjsToMonthDay(date);
    if (!start) return;

    const updated: SeasonMonthDays = {
      start,
      end: endDateCustomized.current
        ? seasonMonthDays.end
        : getDefaultEndMonthDay(start),
    };
    onChange(updated);
  };

  const handleEndChange = (date: Dayjs | null) => {
    const end = dayjsToMonthDay(date);
    if (!end) return;

    endDateCustomized.current = true;
    onChange({ ...seasonMonthDays, end });
  };

  return (
    <Stack spacing={1}>
      {index > 0 && (
        <Typography variant="labelMedium" sx={{
          color: "text.secondary"
        }}>
          Season {index + 1}
        </Typography>
      )}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          flexWrap: "wrap"
        }}>
        <DateField
          label="Start"
          format="MMM D"
          value={monthDayToDayjs(seasonMonthDays.start)}
          onChange={handleStartChange}
          size="small"
          sx={{ width: 100 }}
        />

        <Typography variant="bodyMedium" sx={{
          color: "text.secondary"
        }}>
          –
        </Typography>

        <DateField
          label="End"
          format="MMM D"
          value={monthDayToDayjs(seasonMonthDays.end)}
          onChange={handleEndChange}
          size="small"
          sx={{ width: 100 }}
        />

        {canDelete && (
          <IconButton size="small" onClick={onDelete} aria-label="Remove season">
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
}

export interface OrgSeasonMonthDaysEditorProps {
  value: SeasonMonthDays[];
  onChange: (value: SeasonMonthDays[]) => void;
  maxSeasons?: number;
  label?: string;
}

export default function OrgSeasonMonthDaysEditor({
  value,
  onChange,
  maxSeasons = 4,
  label = 'Default Season Dates',
}: OrgSeasonMonthDaysEditorProps): ReactElement {
  const handleAdd = () => {
    if (value.length >= maxSeasons) return;

    const newStart: MonthDay = { month: 1, day: 1 };
    const newEntry: SeasonMonthDays = {
      start: newStart,
      end: getDefaultEndMonthDay(newStart),
    };
    onChange([...value, newEntry]);
  };

  const handleChange = (index: number, updated: SeasonMonthDays) => {
    const newValue = [...value];
    newValue[index] = updated;
    onChange(newValue);
  };

  const handleDelete = (index: number) => {
    if (value.length <= 1) return;
    const newValue = value.filter((_, i) => i !== index);
    onChange(newValue);
  };

  return (
    <Stack spacing={.5}>
      <Typography variant="labelLarge" sx={{
        color: "text.secondary"
      }}>{label}</Typography>
      <Stack spacing={1} sx={{pt: 1.5}}>
        {value.map((seasonMonthDays, index) => (
          <SeasonMonthDaysRow
            key={index}
            seasonMonthDays={seasonMonthDays}
            index={index}
            canDelete={value.length > 1}
            onChange={(updated) => handleChange(index, updated)}
            onDelete={() => handleDelete(index)}
          />
        ))}
      </Stack>
      {value.length < maxSeasons && (
        <IconButton
          size="small"
          onClick={handleAdd}
          aria-label="Add another season"
          sx={{ alignSelf: 'flex-start' }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );
}
